import { STORAGE_API_BASE, STORAGE_PREVIEW_BASE } from "@/utils/constant";
import { ACCESS_TOKEN_CHANGED_EVENT, getAccessToken } from "@/utils/tokenManager";

// Page half of public/media-auth-sw.js: it pushes the access token to the
// service worker so protected media can be streamed straight from its url
// instead of being downloaded whole. Until the worker acknowledges the token we
// report "unavailable" and callers fall back to the object-url path.

const ACK_TIMEOUT_MS = 3000;

export type MediaAuthStatus = "pending" | "ready" | "unavailable";

let status: MediaAuthStatus = "pending";
const listeners = new Set<() => void>();

const setStatus = (next: MediaAuthStatus) => {
  if (status === next) return;
  status = next;
  listeners.forEach((listener) => listener());
};

export const getMediaAuthStatus = (): MediaAuthStatus => status;

export const subscribeMediaAuth = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Origins the worker is allowed to sign requests for. */
const getMediaOrigins = (): string[] => {
  const origins = [STORAGE_PREVIEW_BASE, STORAGE_API_BASE]
    .filter(Boolean)
    .map((base) => {
      try {
        return new URL(base).origin;
      } catch {
        return "";
      }
    })
    .filter(Boolean);

  return Array.from(new Set(origins));
};

const postConfig = (worker: ServiceWorker): Promise<boolean> =>
  new Promise((resolve) => {
    const channel = new MessageChannel();
    let settled = false;

    const finish = (acknowledged: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      channel.port1.close();
      resolve(acknowledged);
    };

    const timer = window.setTimeout(() => finish(false), ACK_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent) => {
      finish(event.data?.type === "MEDIA_AUTH_READY");
    };

    try {
      worker.postMessage(
        { type: "MEDIA_AUTH_CONFIG", token: getAccessToken() ?? "", origins: getMediaOrigins() },
        [channel.port2],
      );
    } catch {
      finish(false);
    }
  });

export const syncMediaAuth = async (): Promise<void> => {
  const controller = navigator.serviceWorker?.controller;
  if (!controller) {
    setStatus("unavailable");
    return;
  }
  setStatus((await postConfig(controller)) ? "ready" : "unavailable");
};

export const initMediaAuth = (): void => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    setStatus("unavailable");
    return;
  }

  void syncMediaAuth();
  // A freshly installed worker only takes control after it claims the page.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    void syncMediaAuth();
  });
  window.addEventListener(ACCESS_TOKEN_CHANGED_EVENT, () => {
    void syncMediaAuth();
  });
};
